// src/context/ThemeContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

const THEME_KEY = 'sohocard.theme';
const LANG_KEY = 'sohocard.lang';

const localeToDir = (locale) => (locale === 'fa' ? 'rtl' : 'ltr');

export function ThemeProvider({ children }) {
    const [mode, setMode] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
    const [locale, setLocale] = useState(() => localStorage.getItem(LANG_KEY) || 'fa');

    const dir = localeToDir(locale);
    const isRtl = dir === 'rtl';
    const isDark = mode === 'dark';

    // Theme side effects
    useEffect(() => {
        localStorage.setItem(THEME_KEY, mode);
        document.documentElement.setAttribute('data-theme', mode);
        document.documentElement.setAttribute('data-bs-theme', mode);
    }, [mode]);

    // Language side effects
    useEffect(() => {
        localStorage.setItem(LANG_KEY, locale);
        document.documentElement.setAttribute('dir', dir);
        document.documentElement.setAttribute('lang', locale);
    }, [locale, dir]);

    const toggleMode = useCallback(() => {
        setMode((m) => (m === 'light' ? 'dark' : 'light'));
    }, []);

    const toggleDir = useCallback(() => {
        setLocale((l) => (l === 'en' ? 'fa' : 'en'));
    }, []);

    const value = useMemo(
        () => ({
            mode, setMode,
            locale, setLocale,
            dir,
            isDark,
            isRtl,
            toggleMode,
            toggleDir,
        }),
        [mode, locale, dir, isDark, isRtl, toggleMode, toggleDir]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
    return ctx;
}